/**
 * The storefront design-token table.
 *
 * Its own module so `storefrontThemeVars.ts` can resolve a theme without
 * importing index back and forming a cycle. Re-exported from index, so every
 * existing `@loot-os/api-contract` import keeps working.
 */

/**
 * The design tokens a storefront can override — one per shadcn CSS variable,
 * with the default palette's value as its fallback. Keys are the variable name
 * without the `--` prefix, so a token maps to its variable with no translation
 * table: `card-foreground` -> `--card-foreground`. Defaults mirror :root in
 * @loot-os/ui globals.css; they are the hex equivalents of its oklch values, so
 * a storefront that overrides nothing still renders the stock palette.
 * Grouped because the only consumer that enumerates them is a settings editor.
 */
export const STOREFRONT_TOKEN_GROUPS = [
  {
    label: "Base",
    description: "Page background and body text.",
    tokens: [
      { key: "background", label: "Background", default: "#0b0d16" },
      { key: "foreground", label: "Foreground (text)", default: "#e4e8ef" },
      { key: "border", label: "Border", default: "#22283a" },
      { key: "input", label: "Input border", default: "#22283a" },
      { key: "ring", label: "Focus ring", default: "#9087f6" },
    ],
  },
  {
    label: "Surfaces",
    description: "Cards, popovers and muted panels layered on the background.",
    tokens: [
      { key: "card", label: "Card", default: "#121521" },
      { key: "card-foreground", label: "Card text", default: "#e4e8ef" },
      { key: "popover", label: "Popover", default: "#121521" },
      { key: "popover-foreground", label: "Popover text", default: "#e4e8ef" },
      { key: "muted", label: "Muted", default: "#202638" },
      { key: "muted-foreground", label: "Muted text", default: "#8992a5" },
    ],
  },
  {
    label: "Brand",
    description: "Primary actions, secondary buttons and hover accents.",
    tokens: [
      { key: "primary", label: "Primary", default: "#9087f6" },
      { key: "primary-foreground", label: "Primary text", default: "#0b0d16" },
      { key: "secondary", label: "Secondary", default: "#202638" },
      { key: "secondary-foreground", label: "Secondary text", default: "#e4e8ef" },
      { key: "accent", label: "Accent (hover)", default: "#202638" },
      { key: "accent-foreground", label: "Accent text", default: "#e4e8ef" },
    ],
  },
  {
    label: "State",
    description: "Destructive actions and errors.",
    tokens: [
      { key: "destructive", label: "Destructive", default: "#fb2c36" },
      { key: "destructive-foreground", label: "Destructive text", default: "#f6f9fc" },
    ],
  },
  {
    label: "Charts",
    description: "Categorical series colors for stats and leaderboards.",
    tokens: [
      { key: "chart-1", label: "Chart 1", default: "#9087f6" },
      { key: "chart-2", label: "Chart 2", default: "#facc15" },
      { key: "chart-3", label: "Chart 3", default: "#31bafb" },
      { key: "chart-4", label: "Chart 4", default: "#7ccf00" },
      { key: "chart-5", label: "Chart 5", default: "#f0a68c" },
    ],
  },
  {
    label: "Sidebar",
    description: "Only used by storefronts that render sidebar navigation.",
    tokens: [
      { key: "sidebar", label: "Sidebar", default: "#0f121d" },
      { key: "sidebar-foreground", label: "Sidebar text", default: "#e4e8ef" },
      { key: "sidebar-primary", label: "Sidebar primary", default: "#9087f6" },
      { key: "sidebar-primary-foreground", label: "Sidebar primary text", default: "#0b0d16" },
      { key: "sidebar-accent", label: "Sidebar accent", default: "#202638" },
      { key: "sidebar-accent-foreground", label: "Sidebar accent text", default: "#e4e8ef" },
      { key: "sidebar-border", label: "Sidebar border", default: "#22283a" },
      { key: "sidebar-ring", label: "Sidebar focus ring", default: "#9087f6" },
    ],
  },
] as const;

export type StorefrontTokenKey = (typeof STOREFRONT_TOKEN_GROUPS)[number]["tokens"][number]["key"];

export const STOREFRONT_TOKEN_KEYS = STOREFRONT_TOKEN_GROUPS.flatMap((group) =>
  group.tokens.map((token) => token.key),
) as StorefrontTokenKey[];

/** The stock palette, keyed by token — the value used wherever a tenant sets nothing. */
export const STOREFRONT_TOKEN_DEFAULTS = Object.fromEntries(
  STOREFRONT_TOKEN_GROUPS.flatMap((group) => group.tokens.map((t) => [t.key, t.default])),
) as Record<StorefrontTokenKey, string>;

/** Merges stored overrides onto the defaults, so callers always get a complete palette. */
export function resolveStorefrontTokens(
  overrides?: Partial<Record<StorefrontTokenKey, string>> | null,
): Record<StorefrontTokenKey, string> {
  const resolved = { ...STOREFRONT_TOKEN_DEFAULTS };
  for (const [key, value] of Object.entries(overrides ?? {})) {
    // An empty string means "unset" — keep the default rather than blanking the variable.
    if (typeof value === "string" && value.trim() !== "") {
      resolved[key as StorefrontTokenKey] = value.trim();
    }
  }
  return resolved;
}
